import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import {
  Gift, Plus, Edit, Trash2, Search, CreditCard, Euro, Clock,
  CheckCircle2, XCircle, Copy, ExternalLink, Loader2, Tag, Users
} from 'lucide-react';

const STATUS_MAP = {
  active: { label: 'Attiva', color: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20', icon: CheckCircle2 },
  partially_used: { label: 'Parziale', color: 'bg-amber-500/10 text-amber-700 border-amber-500/20', icon: CreditCard },
  exhausted: { label: 'Esaurita', color: 'bg-gray-500/10 text-gray-600 border-gray-500/20', icon: XCircle },
  expired: { label: 'Scaduta', color: 'bg-red-500/10 text-red-700 border-red-500/20', icon: Clock },
};

const DESIGNS = [
  { id: 'classic', label: 'Classico' },
  { id: 'birthday', label: 'Compleanno' },
  { id: 'holiday', label: 'Festività' },
  { id: 'thank_you', label: 'Ringraziamento' },
];

const GiftCardsPage = () => {
  const { API, getAuthHeaders } = useAuth();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isRedeemOpen, setIsRedeemOpen] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [form, setForm] = useState({ value: '', recipient_name: '', recipient_email: '', sender_name: '', message: '', design: 'classic', color: '#14B8A6', expiry_days: 365 });
  const [redeemCode, setRedeemCode] = useState('');
  const [redeemAmount, setRedeemAmount] = useState('');
  const [redeemResult, setRedeemResult] = useState(null);
  const [redeemLoading, setRedeemLoading] = useState(false);

  const fetchCards = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/giftcards`, getAuthHeaders());
      setCards(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [API, getAuthHeaders]);

  useEffect(() => { fetchCards(); }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editingCard) {
        await axios.put(`${API}/giftcards/${editingCard.id}`, form, getAuthHeaders());
      } else {
        await axios.post(`${API}/giftcards`, form, getAuthHeaders());
      }
      resetForm();
      fetchCards();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa gift card?')) return;
    try {
      await axios.delete(`${API}/giftcards/${id}`, getAuthHeaders());
      fetchCards();
    } catch (err) { alert('Errore nell\'eliminazione'); }
  };

  const resetForm = () => {
    setForm({ value: '', recipient_name: '', recipient_email: '', sender_name: '', message: '', design: 'classic', color: '#14B8A6', expiry_days: 365 });
    setEditingCard(null);
    setIsFormOpen(false);
  };

  const openEdit = (card) => {
    setEditingCard(card);
    setForm({
      value: card.original_value, recipient_name: card.recipient_name || '', recipient_email: card.recipient_email || '',
      sender_name: card.sender_name || '', message: card.message || '', design: card.design || 'classic',
      color: card.color || '#14B8A6', expiry_days: 365
    });
    setIsFormOpen(true);
  };

  const handleRedeem = async () => {
    setRedeemLoading(true);
    try {
      const res = await axios.post(`${API}/giftcards/redeem`, {
        code: redeemCode, amount: parseFloat(redeemAmount) || 0
      }, getAuthHeaders());
      setRedeemResult(res.data);
      if (res.data.action === 'redeemed') fetchCards();
    } catch (err) {
      setRedeemResult({ error: err.response?.data?.detail || 'Errore' });
    } finally { setRedeemLoading(false); }
  };

  // Stats
  const totalValue = cards.reduce((s, c) => s + c.original_value, 0);
  const totalRemaining = cards.reduce((s, c) => s + c.remaining_value, 0);
  const activeCards = cards.filter(c => c.status === 'active' || c.status === 'partially_used').length;

  const filtered = cards.filter(c =>
    c.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (c.recipient_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (c.sender_name || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="giftcards-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-outfit">Gift Card</h1>
          <p className="text-muted-foreground mt-1">Crea e gestisci le gift card del tuo salone</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={isRedeemOpen} onOpenChange={(o) => { setIsRedeemOpen(o); if (!o) { setRedeemCode(''); setRedeemAmount(''); setRedeemResult(null); } }}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="redeem-btn"><Tag className="w-4 h-4 mr-2" />Riscatta</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader><DialogTitle className="font-outfit">Riscatta Gift Card</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Codice Gift Card</Label>
                  <Input value={redeemCode} onChange={e => setRedeemCode(e.target.value.toUpperCase())} placeholder="GC-XXXX-XXXX" className="font-mono text-lg tracking-wider" data-testid="redeem-code-input" />
                </div>
                {!redeemResult && (
                  <Button onClick={() => { setRedeemAmount('0'); handleRedeem(); }} disabled={!redeemCode.trim() || redeemLoading} className="w-full" data-testid="redeem-lookup-btn">
                    {redeemLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}Cerca
                  </Button>
                )}
                {redeemResult?.error && (
                  <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">{redeemResult.error}</div>
                )}
                {redeemResult?.card && (
                  <div className="space-y-4">
                    <div className="p-4 rounded-xl border" style={{ borderColor: redeemResult.card.color + '40' }}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-mono font-bold">{redeemResult.card.code}</span>
                        <Badge variant="outline" className={STATUS_MAP[redeemResult.card.status]?.color}>{STATUS_MAP[redeemResult.card.status]?.label}</Badge>
                      </div>
                      <div className="text-center py-3">
                        <p className="text-[10px] sm:text-xs text-muted-foreground leading-tight">Saldo Disponibile</p>
                        <p className="text-3xl font-bold" style={{ color: redeemResult.card.color }}>€{redeemResult.card.remaining_value.toFixed(2)}</p>
                      </div>
                      {redeemResult.card.recipient_name && <p className="text-sm text-muted-foreground">Destinatario: {redeemResult.card.recipient_name}</p>}
                    </div>
                    {redeemResult.action === 'redeemed' ? (
                      <div className="p-3 rounded-lg bg-emerald-500/10 text-emerald-700 text-sm text-center">
                        <CheckCircle2 className="w-5 h-5 mx-auto mb-1" />Riscattati €{redeemResult.amount.toFixed(2)} — Nuovo saldo: €{redeemResult.new_remaining.toFixed(2)}
                      </div>
                    ) : redeemResult.card.status === 'active' || redeemResult.card.status === 'partially_used' ? (
                      <div className="space-y-2">
                        <Label>Importo da riscattare (€)</Label>
                        <Input type="number" step="0.01" min="0.01" max={redeemResult.card.remaining_value} value={redeemAmount} onChange={e => setRedeemAmount(e.target.value)} data-testid="redeem-amount-input" />
                        <Button onClick={() => handleRedeem()} disabled={!redeemAmount || parseFloat(redeemAmount) <= 0 || redeemLoading} className="w-full" data-testid="redeem-confirm-btn">
                          {redeemLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                          Conferma Riscatto
                        </Button>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={isFormOpen} onOpenChange={(o) => { if (!o) resetForm(); else setIsFormOpen(true); }}>
            <DialogTrigger asChild>
              <Button data-testid="create-giftcard-btn"><Plus className="w-4 h-4 mr-2" />Crea Gift Card</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-outfit">{editingCard ? 'Modifica Gift Card' : 'Nuova Gift Card'}</DialogTitle></DialogHeader>
              <form onSubmit={handleSave} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Valore (€) *</Label>
                    <Input type="number" step="0.01" min="1" value={form.value} onChange={e => setForm(p => ({ ...p, value: e.target.value }))} required data-testid="gc-value-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Colore</Label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={form.color} onChange={e => setForm(p => ({ ...p, color: e.target.value }))} className="w-10 h-10 rounded cursor-pointer border" />
                      <Input value={form.color} onChange={e => setForm(p => ({ ...p, color: e.target.value }))} className="font-mono text-xs" />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Destinatario</Label>
                    <Input value={form.recipient_name} onChange={e => setForm(p => ({ ...p, recipient_name: e.target.value }))} placeholder="Nome destinatario" data-testid="gc-recipient-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Email Destinatario</Label>
                    <Input type="email" value={form.recipient_email} onChange={e => setForm(p => ({ ...p, recipient_email: e.target.value }))} placeholder="email@esempio.com" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Da parte di</Label>
                  <Input value={form.sender_name} onChange={e => setForm(p => ({ ...p, sender_name: e.target.value }))} placeholder="Nome mittente" />
                </div>
                <div className="space-y-2">
                  <Label>Messaggio personalizzato</Label>
                  <Textarea value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))} placeholder="Buon compleanno! Goditi un trattamento speciale..." rows={2} data-testid="gc-message-input" />
                </div>
                <div className="space-y-2">
                  <Label>Design</Label>
                  <div className="grid grid-cols-4 gap-2">
                    {DESIGNS.map(d => (
                      <button key={d.id} type="button" onClick={() => setForm(p => ({ ...p, design: d.id }))}
                        className={`p-2 rounded-lg border text-center text-xs transition-all ${form.design === d.id ? 'ring-2 ring-primary border-primary' : 'hover:border-muted-foreground/30'}`}
                        data-testid={`design-${d.id}`}
                      >{d.label}</button>
                    ))}
                  </div>
                </div>

                {/* Live Preview */}
                <div className="space-y-2">
                  <Label className="text-[10px] sm:text-xs text-muted-foreground leading-tight">Anteprima</Label>
                  <GiftCardVisual value={parseFloat(form.value) || 0} color={form.color} recipient={form.recipient_name} message={form.message} design={form.design} code="GC-XXXX-XXXX" />
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={resetForm}>Annulla</Button>
                  <Button type="submit" data-testid="save-gc-btn">{editingCard ? 'Aggiorna' : 'Crea'} Gift Card</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-2 sm:gap-4">
        <Card><CardContent className="p-2 sm:p-4 text-center">
          <Gift className="w-5 h-5 mx-auto text-primary mb-1" />
          <p className="text-lg sm:text-2xl font-bold">{cards.length}</p><p className="text-[10px] sm:text-xs text-muted-foreground leading-tight">Totale</p>
        </CardContent></Card>
        <Card><CardContent className="p-2 sm:p-4 text-center">
          <CheckCircle2 className="w-5 h-5 mx-auto text-emerald-500 mb-1" />
          <p className="text-lg sm:text-2xl font-bold">{activeCards}</p><p className="text-[10px] sm:text-xs text-muted-foreground leading-tight">Attive</p>
        </CardContent></Card>
        <Card><CardContent className="p-2 sm:p-4 text-center">
          <Euro className="w-5 h-5 mx-auto text-blue-500 mb-1" />
          <p className="text-lg sm:text-2xl font-bold">€{totalValue.toFixed(0)}</p><p className="text-[10px] sm:text-xs text-muted-foreground leading-tight">Valore Emesso</p>
        </CardContent></Card>
        <Card><CardContent className="p-2 sm:p-4 text-center">
          <CreditCard className="w-5 h-5 mx-auto text-amber-500 mb-1" />
          <p className="text-lg sm:text-2xl font-bold">€{(totalValue - totalRemaining).toFixed(0)}</p><p className="text-[10px] sm:text-xs text-muted-foreground leading-tight">Riscattato</p>
        </CardContent></Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input placeholder="Cerca per codice, destinatario o mittente..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9" data-testid="search-gc" />
      </div>

      {/* Cards List */}
      {filtered.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(card => {
            const status = STATUS_MAP[card.status];
            return (
              <Card key={card.id} className="overflow-hidden" data-testid={`gc-${card.id}`}>
                <div className="h-2" style={{ backgroundColor: card.color }} />
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-mono font-bold text-sm tracking-wider">{card.code}</span>
                    <Badge variant="outline" className={status?.color + ' text-[10px]'}>{status?.label}</Badge>
                  </div>
                  <div className="flex items-end justify-between">
                    <div>
                      <p className="text-[10px] sm:text-xs text-muted-foreground leading-tight">Valore</p>
                      <p className="text-xl font-bold" style={{ color: card.color }}>€{card.original_value.toFixed(2)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] sm:text-xs text-muted-foreground leading-tight">Saldo</p>
                      <p className="text-xl font-bold">€{card.remaining_value.toFixed(2)}</p>
                    </div>
                  </div>
                  {card.recipient_name && <p className="text-[10px] sm:text-xs text-muted-foreground leading-tight">Per: <strong>{card.recipient_name}</strong></p>}
                  {card.sender_name && <p className="text-[10px] sm:text-xs text-muted-foreground leading-tight">Da: {card.sender_name}</p>}
                  <div className="flex items-center justify-between pt-2 border-t">
                    <span className="text-[10px] text-muted-foreground">
                      {card.source === 'online' ? 'Acquisto online' : 'Creata manualmente'} · Scade {new Date(card.expires_at).toLocaleDateString('it-IT')}
                    </span>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { navigator.clipboard.writeText(card.code); alert('Codice copiato!'); }}><Copy className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(card)} data-testid={`edit-gc-${card.id}`}><Edit className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(card.id)} data-testid={`delete-gc-${card.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card><CardContent className="py-16 text-center">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4"><Gift className="w-8 h-8 text-primary" /></div>
          <h3 className="font-semibold text-lg mb-1">Nessuna gift card</h3>
          <p className="text-muted-foreground text-sm">Crea la tua prima gift card per iniziare.</p>
        </CardContent></Card>
      )}
    </div>
  );
};

// ─── Gift Card Visual Component ───
const GiftCardVisual = ({ value, color, recipient, message, design, code, salonName }) => {
  const designEmoji = { classic: '', birthday: '🎂', holiday: '🎄', thank_you: '💐' };
  return (
    <div className="rounded-xl overflow-hidden shadow-lg max-w-[340px] mx-auto" style={{ background: `linear-gradient(135deg, ${color}, ${color}dd)` }} data-testid="gc-visual">
      <div className="p-5 text-white">
        <div className="flex items-center justify-between mb-4">
          <span className="text-xs font-medium opacity-80 uppercase tracking-wider">Gift Card {designEmoji[design]}</span>
          {salonName && <span className="text-xs font-medium opacity-80">{salonName}</span>}
        </div>
        <div className="text-center py-4">
          <p className="text-4xl font-bold">€{(value || 0).toFixed(2)}</p>
          {recipient && <p className="text-sm mt-2 opacity-90">Per: {recipient}</p>}
          {message && <p className="text-xs mt-1 opacity-75 italic">"{message}"</p>}
        </div>
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/20">
          <span className="font-mono text-sm tracking-wider opacity-90">{code}</span>
          <Gift className="w-5 h-5 opacity-60" />
        </div>
      </div>
    </div>
  );
};

export { GiftCardVisual };
export default GiftCardsPage;
